import { StripeInvoice } from "./stripeClient";

export type ContentLanguage = "en" | "fr" | "id" | "fil" | "th" | "ko" | "ja" | "de" | "es" | "pt" | "ar";

export const CONTENT_LANGUAGES: { code: ContentLanguage; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "\ud83c\uddfa\ud83c\uddf8" },
  { code: "fr", label: "French", flag: "\ud83c\uddeb\ud83c\uddf7" },
  { code: "id", label: "Indonesian", flag: "\ud83c\uddee\ud83c\udde9" },
  { code: "fil", label: "Filipino", flag: "\ud83c\uddf5\ud83c\udded" },
  { code: "th", label: "Thai", flag: "\ud83c\uddf9\ud83c\udded" },
  { code: "ko", label: "Korean", flag: "\ud83c\uddf0\ud83c\uddf7" },
  { code: "ja", label: "Japanese", flag: "\ud83c\uddef\ud83c\uddf5" },
  { code: "de", label: "German", flag: "\ud83c\udde9\ud83c\uddea" },
  { code: "es", label: "Spanish", flag: "\ud83c\uddea\ud83c\uddf8" },
  { code: "pt", label: "Portuguese", flag: "\ud83c\uddf5\ud83c\uddf9" },
  { code: "ar", label: "Arabic", flag: "\ud83c\uddf8\ud83c\udde6" },
];

export type DeviceType = "android" | "ios" | "web";

// SQS messages
export type QueueMessage = {
  type: "QueueMessage";
  isDev: boolean;
  authToken: string;
  webhookUrl: string;
  delaySeconds?: number;
  event: QueueMessageOnRewriteChapter;
};

export type QueueMessageOnRewriteChapter = {
  type: "onRewriteChapter";
  bookId: string;
  chapterNumber: number;
  isLookAhead: boolean;
  lookAheadOrigin?: number;
  lang: ContentLanguage;
};

export type RemoteConfig = {
  country: string;
  webPlans: {
    weekly: SubscriptionPlan;
    annual: SubscriptionPlan;
  };
  posthog: {
    enableSessionRecording: boolean;
  };
  featuredBooks?: { bookId: string; coverUrl: string }[];
};

export type User = {
  id: string; // user_{uuid}
  email: string;
  emailVerified: boolean;
  ip: string;
  country: string;
  oauthProvider: "google" | "apple" | "email" | "mobile";
  deviceLanguage: string;
  stripeCustomerId?: string;
  theme?: "light" | "dark" | "system";
  oneTimeCode?: number;
  deviceType: DeviceType;
  unverifiedEmail?: string;
  deviceTimezone: string;
  createdAt: string;
  updatedAt: string;
};

export type EmailValidation = {
  email: string;
  isValid: boolean;
  status: string;
  createdAt: string;
  apiResponseJson: string;
};

export type Subscription = {
  userId: string;
  type: "REVENUE_CAT" | "STRIPE";
  createdAt: string;
  updatedAt?: string;
  email?: string;
  isActive?: boolean;
  revenueCatEvent?: {
    cancel_reason?: string;
    expiration_reason?: string;
    subscriber_attributes?: Record<string, never>;
    is_family_share?: boolean;
    product_id?: string;
    period_type?: string;
    environment?: string;
    entitlement_ids?: string[];
    presented_offering_id?: string;
    transaction_id?: string;
    transferred_to?: string[];
    transferred_from?: string[];
    original_transaction_id?: string;
    country_code?: string;
    app_user_id?: string;
    aliases?: string[];
    original_app_user_id?: string;
    currency?: string;
    store?: string;
    type?: string;
    id?: string;
    app_id?: string;
    event_timestamp_ms?: number;
    purchased_at_ms?: number;
    expiration_at_ms?: number;
    price?: number;
    price_in_purchased_currency?: number;
    takehome_percentage?: number;
    tax_percentage?: number;
    commission_percentage?: number;
    renewal_number?: number;
  };
  stripeInvoice?: StripeInvoice;
};

export type BookCategory =
  | "Romance"
  | "Werewolf"
  | "Dark Romance"
  | "Fantasy"
  | "Drama"
  | "Mystery & Crime"
  | "YA Teen"
  | "Action & Adventure"
  | "Paranormal"
  | "Horror & Thriller"
  | "Sci-Fi & Romance"
  | "LGBTQ"
  | "Erotica"
  | "Historical Fiction"
  | "Family Saga"
  | "Literary Fiction";

export type CoverGeneration = {
  bookId: string;
  originalCoverUrl: string;
  generatedImageUrl: string;
  prompt: string;
  model: string;
  generatedAt: string;
};

export type OriginalBook = {
  bookId: string;
  title?: string;
  coverUrl?: string;
  description?: string;
  authorName?: string;
  aiTitle: string;
  aiCoverUrl: string;
  aiCoverBlurDataUri?: string;
  aiAuthorName: string;
  aiDescription: string;
  grade: string;
  wordsCount: number;
  views: number;
  publishedAt: string;
  opTagsNames: string[];
  tagsNames: BookCategory[];
  chapterCount: number;
  bookScore: number;
  bookClassName: string;
  isShort: boolean;
  addBookShelf: number;
  hot: number;
  completed: boolean;
  href: string;
  chapters: { [chapterNumber: number]: string };
  paywallAfterChapter?: number;
};

export type Book = Pick<
  OriginalBook,
  | "bookId"
  | "aiTitle"
  | "aiCoverUrl"
  | "aiCoverBlurDataUri"
  | "aiDescription"
  | "chapterCount"
  | "paywallAfterChapter"
  | "hot"
  | "completed"
  | "tagsNames"
>;

export type GoogleAPIUserProfile = {
  sub: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  email: string;
  email_verified: boolean;
};

export type AuthUser = {
  userId: string;
  email: string;
  exp?: number;
  iat?: number;
  nbf?: number;
  isGuest?: boolean;
  isAuthenticated?: boolean;
  ip?: string;
  country?: string;
};

export type SubscriptionPlan =
  | "low_tier_weekly"
  | "low_tier_monthly"
  | "low_tier_annual"
  | "mid_tier_weekly"
  | "mid_tier_monthly"
  | "mid_tier_annual"
  | "top_tier_weekly"
  | "top_tier_monthly"
  | "top_tier_annual";

export type RevenueCatPackageId =
  | "low_tier_weekly"
  | "low_tier_monthly"
  | "low_tier_annual"
  | "mid_tier_weekly"
  | "mid_tier_monthly"
  | "mid_tier_annual"
  | "top_tier_weekly"
  | "top_tier_monthly"
  | "top_tier_annual";

export type StoryBible = {
  bookId: string;
  originalTitle: string;
  aiTitle: string;
  aiAuthorName: string;
  aiDescription: string;
  genre: BookCategory[];
  tone: string;
  settingSummary: string;
  narrativePOV: string;
  characterMap: {
    [originalName: string]: {
      aiName: string;
      role: string;
      briefDescription: string;
    };
  };
  placeMap: { [originalName: string]: string };
  styleGuide: string;
  styleFingerprint?: string;
  voiceSample?: string;
  spiceGuidance?: string;
  generatedAt: string;
  updatedAt: string;
};

export type AIChapter = {
  bookId: string;
  chapterNumber: number;
  aiContent: string;
  chapterSummary: string;
  rollingSummary: string;
  originalWordCount?: number;
  aiWordCount: number;
  generatedAt: string;
  modelUsed: string;
  generationMethod?: "rewrite";
};

export type ForeshadowingEntry = {
  revealChapter: number;
  revealDescription: string;
  hints: { plantInChapter: number; hint: string }[];
};

export type StoryArc = {
  bookId: string;
  totalChapters: number;
  storyMap: string;
  chapterPlans: { [chapterNumber: string]: string };
  chapterSummaries: { [chapterNumber: string]: string };
  chapterTeasers?: { [chapterNumber: string]: string };
  foreshadowingMap?: ForeshadowingEntry[];
  paywallRecommendation?: { chapter: number; reason: string };
  generatedAt: string;
};

export type ChapterScanResult = {
  summary: string;
  characters: string;
  keyEvents: string;
  wordCount: number;
};

export type ChapterScan = {
  bookId: string;
  chapterScanResults: { [chapterNumber: number]: ChapterScanResult };
  lastCompletedBatch: number;
  generatedAt: string;
};

export type GenerationState = {
  bookId: string;
  frontier: number;
  inProgress: number | null;
  inProgressStartedAt: string | null;
  storyBibleGenerated: boolean;
  updatedAt: string;
};

export type ReadingProgress = {
  lastChapter: number;
  scrollPosition: number;
  lastReadAt: string;
};

export type ReaderSettings = {
  fontSize: number;
  lineHeight: number;
  fontFamily: "serif" | "sans-serif" | "monospace";
  readerTheme: "default" | "sepia" | "dark";
};

export type UserPreferences = {
  userId: string;
  readingProgress: { [bookId: string]: ReadingProgress };
  bookshelf: string[];
  likedBooks: string[];
  readerSettings: ReaderSettings;
  updatedAt: string;
};

export type ApiRequestPreference = {
  action: "Preference";
  payload: { type: "get" } | { type: "set"; payload: { preferences: Partial<UserPreferences> } };
};

export type ApiResponsePreference = {
  action: "Preference";
  error?: string;
  data?: { preferences?: UserPreferences };
};

export type ApiRequestBook = {
  action: "Book";
  payload:
    | { type: "getAIChapter"; payload: { bookId: string; chapterNumber: number; lang: ContentLanguage } }
    | { type: "getGenerationStatus"; payload: { bookId: string; lang: ContentLanguage } }
    | { type: "get"; payload: { bookId: string; lang: ContentLanguage } }
    | { type: "home"; payload: { lang: ContentLanguage } };
};

export type ApiRequestAuthenticate = {
  action: "Authenticate";
  payload:
    | { type: "OnGuestSignIn"; payload: { deviceId: string; deviceLanguage: string; deviceTimezone: string; deviceType: DeviceType } }
    | {
        type: "OnGoogleSignIn";
        payload: {
          googleAccessToken: string;
          deviceLanguage: string;
          deviceTimezone: string;
          deviceType: DeviceType;
        };
      }
    | { type: "OnOneTimeCodeSubmit"; payload: { oneTimeCode: number; email: string } }
    | {
        type: "OnOneTimeCodeRequested";
        payload: {
          email: string;
          deviceLanguage: string;
          deviceTimezone: string;
          deviceType: DeviceType;
        };
      };
};

export type ApiResponseAuthenticate = {
  action: "Authenticate";
  error?: "UserNotFound" | "InvalidOneTimeCode" | "InvalidAccessToken" | string;
  data?: { accessToken: string };
};

export type ApiRequestUser = {
  action: "User";
  payload:
    | { type: "get" }
    | { type: "update"; payload: { updates: Partial<User> } }
    | {
        type: "nativeSignIn";
        payload: { action: "request"; payload: { email: string } } | { action: "confirm"; payload: { oneTimeCode: number } };
      };
};

export type ApiResponseUser = {
  action: "User";
  error?: string;
  data?: { user?: User; accessToken?: string };
};

export type ApiRequestSubscription = {
  action: "Subscription";
  payload:
    | { type: "checkout"; payload: { baseAppUrl: string; subscriptionPlan: SubscriptionPlan } }
    | { type: "billingPortal"; payload: { baseAppUrl: string } }
    | { type: "status" };
};

export type ApiResponseSubscription = {
  action: "Subscription";
  error?: string;
  data?: { url?: string; isActive?: boolean };
};

export type ApiResponseBook = {
  action: "Book";
  error?: string;
  data?: {
    aiChapter?: AIChapter;
    generationState?: GenerationState;
    status?: "ready" | "generating" | "queued" | "requires_previous" | "subscription_required";
    retryAfterSeconds?: number;
    book?: Book;
    books?: Book[];
    nextCursor?: string;
    homeBooks?: Book[];
  };
};

export type ApiRequest = ApiRequestAuthenticate | ApiRequestSubscription | ApiRequestUser | ApiRequestBook | ApiRequestPreference;

export type ApiResponse = ApiResponseAuthenticate | ApiResponseSubscription | ApiResponseUser | ApiResponseBook | ApiResponsePreference;
